// src/components/player/tiles/Tile1Performance30dPitcher.js
// Tile 1: 30-Day Pitcher Performance Benchmarking
// Displays pitcher's 30-day performance with MLB and league benchmarks
// Role-based: Starter, Closer, or Reliever

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { TrendingUp } from 'lucide-react';

// Cluster-D.1: league category code (W, SV, SO, QS) → backend stat field name + display label
const STAT_FIELD_MAP = {
  W: { field: 'wins', label: 'W' },
  SV: { field: 'saves', label: 'SV' },
  ERA: { field: 'era', label: 'ERA' },
  WHIP: { field: 'whip', label: 'WHIP' },
  SO: { field: 'strikeouts_pitched', label: 'K' },
  K: { field: 'strikeouts_pitched', label: 'K' },
  QS: { field: 'quality_starts', label: 'QS' },
  L: { field: 'losses', label: 'L' },
  H: { field: 'holds', label: 'HLD' },
  HLD: { field: 'holds', label: 'HLD' },
  BS: { field: 'blown_saves', label: 'BS' },
  IP: { field: 'innings_pitched', label: 'IP' },
  'K/9': { field: 'k_per_9', label: 'K/9' },
  'BB/9': { field: 'bb_per_9', label: 'BB/9' },
};

const Tile1Performance30dPitcher = ({ data, loading, error, leagueScoring = null }) => {
  // Tile wrapper component
  const Tile = ({ children }) => (
    <div className={`${dynastyTheme.components.card.base} h-full`}>
      {children}
    </div>
  );

  // Pitcher Performance Grid Component
  // Cluster-D.1: column set driven by league_settings.scoring_categories. Replaces
  // role-based hardcoded arrays (starter / closer / reliever) which ignored league
  // pitcher categories entirely. HCML pitching = [W, SV, ERA, WHIP, SO, QS] now
  // renders consistently for any pitcher role.
  const TilePitcherGrid = ({ data, showLeague = true }) => {
    // Return early if no data
    if (!data || !data.player) {
      return (
        <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
          No performance data available
        </div>
      );
    }

    const { player, mlb_benchmark, league_benchmark, comparisons } = data;
    const role = player.role || 'starter';

    // Build column list from league_settings.scoring_categories.pitching
    // Filter to categories the backend actually returned stats for.
    const playerStats = player.stats || {};
    const pitcherCategories = (leagueScoring?.pitching) || [];
    const mappedCols = pitcherCategories
      .map((code) => STAT_FIELD_MAP[code])
      .filter((entry) => entry && playerStats[entry.field] !== undefined);

    const headers = mappedCols.map((c) => c.label);
    const statKeys = mappedCols.map((c) => c.field);

    // Format number display
    const formatStat = (value, key) => {
      if (value === null || value === undefined) return '0';
      if (key === 'era' || key === 'whip' || key === 'bb_per_9' || key === 'k_per_9') {
        return Number(value).toFixed(2);
      }
      return Math.round(value).toString();
    };

    // Get color based on comparison
    const getStatColor = (comparison, stat) => {
      if (!comparison || !comparison[stat]) return dynastyTheme.classes.text.white;
      if (comparison[stat] === 'better') return dynastyTheme.classes.text.success;
      if (comparison[stat] === 'worse') return dynastyTheme.classes.text.error;
      return dynastyTheme.classes.text.white;
    };

    // Get trend indicator
    const getTrendIndicator = (comparisons) => {
      if (!comparisons?.vs_mlb) return '';
      const betterCount = Object.values(comparisons.vs_mlb).filter(v => v === 'better').length;
      const totalStats = Object.keys(comparisons.vs_mlb).length;
      if (betterCount > totalStats * 0.6) return '↑';
      if (betterCount < totalStats * 0.4) return '↓';
      return '→';
    };

    const trendIndicator = getTrendIndicator(comparisons);
    const trendColor = trendIndicator === '↑' ? dynastyTheme.classes.text.success : 
                       trendIndicator === '↓' ? dynastyTheme.classes.text.error : 
                       dynastyTheme.classes.text.neutral;

    // Calculate dynamic grid columns (1 label column + N stat columns).
    // The label column is a FIXED width (not `auto`) so it is identical across the
    // header / You / MLB / League rows — each row is its own grid, and an `auto`
    // first column sized to its own label (empty header vs "You: →" vs "MLB:" vs
    // "League:") pushed the 1fr stat columns to different x-positions per row,
    // misaligning every column from its header. Fixed width = columns line up.
    const gridClass = `grid gap-1 text-sm`;
    const gridStyle = { gridTemplateColumns: `3.75rem repeat(${statKeys.length}, 1fr)` };

    return (
      <div className="space-y-1">
        {/* Headers */}
        <div className={`${gridClass} font-bold`} style={gridStyle}>
          <div></div>  {/* Empty cell for row labels */}
          {headers.map(h => (
            <div key={h} className={`text-center ${dynastyTheme.classes.text.primary}`}>
              {h}
            </div>
          ))}
        </div>
        
        {/* Player row */}
        <div className={`${gridClass} items-center`} style={gridStyle}>
          <div className={`${dynastyTheme.classes.text.white} flex items-center`}>
            You:
            {trendIndicator && (
              <span className={`ml-1 text-sm ${trendColor}`}>{trendIndicator}</span>
            )}
          </div>
          {statKeys.map(key => (
            <div 
              key={key}
              className={`text-center ${getStatColor(comparisons?.vs_mlb, key)}`}
            >
              {formatStat(player.stats?.[key], key)}
            </div>
          ))}
        </div>
        
        {/* MLB benchmark row - FIXED: Use .pitching not .hitting */}
        {mlb_benchmark && mlb_benchmark.pitching && (
          <div className={gridClass} style={gridStyle}>
            <div className={dynastyTheme.classes.text.neutralLight}>MLB:</div>
            {statKeys.map(key => (
              <div key={key} className={`text-center ${dynastyTheme.classes.text.neutralLight}`}>
                {formatStat(mlb_benchmark.pitching?.[key], key)}
              </div>
            ))}
          </div>
        )}
        
        {/* League benchmark row (if present and enabled) - FIXED: Use .pitching not .hitting */}
        {showLeague && league_benchmark && league_benchmark.pitching && (
          <div className={gridClass} style={gridStyle}>
            <div className={dynastyTheme.classes.text.neutralLight}>League:</div>
            {statKeys.map(key => (
              <div key={key} className={`text-center ${dynastyTheme.classes.text.neutralLight}`}>
                {formatStat(league_benchmark.pitching?.[key], key)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Check data structure
  const hasPlayerData = data?.player;
  const hasPlayerStats = data?.player?.stats;
  const hasLeagueBenchmark = data?.league_benchmark;

  return (
    <Tile>
      <div className="pt-1 px-4 pb-3 flex flex-col tile-scroll" style={{ height: '182px', overflow: 'auto' }}>
        <h4 className={`text-base font-semibold ${dynastyTheme.classes.text.primary} mb-3 flex items-center`}>
          <TrendingUp className="w-4 h-4 mr-2" />
          30-Day Performance
        </h4>
        
        {loading ? (
          <div className={`flex-1 flex items-center justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Loading analytics...
            </div>
          </div>
        ) : error ? (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.error} mb-2`}>
              {error}
            </div>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Check console for details
            </div>
          </div>
        ) : hasPlayerData ? (
          <div className="flex-1 flex flex-col">
            {/* If we have the right structure for TilePitcherGrid */}
            {hasPlayerStats ? (
              <div className="flex-1">
                <TilePitcherGrid 
                  data={data}
                  showLeague={hasLeagueBenchmark}
                />
              </div>
            ) : (
              // Fallback display if structure is different
              <div className="flex-1 space-y-2">
                {Object.entries(data.player || {}).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className={`text-xs ${dynastyTheme.classes.text.neutral}`}>{key}</span>
                    <span className={`text-sm ${dynastyTheme.classes.text.white}`}>
                      {typeof value === 'number' ? value.toFixed(2) : value}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {/* Footer with sample size and period info */}
            {data.player && (
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} pt-2 mt-2 border-t border-gray-700`}>
                <div className="flex justify-between">
                  <span>
                    {data.player.period_label || 
                     (data.player.period_days 
                       ? `Last ${data.player.period_days} days` 
                       : 'Recent performance')}
                  </span>
                  <span>{data.player.games} games</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="opacity-75">
                    Role: {data.player.role ? data.player.role.charAt(0).toUpperCase() + data.player.role.slice(1) : 'Unknown'}
                  </span>
                </div>
                {data.mlb_benchmark?.sample_size > 0 && (
                  <div className="text-xs opacity-60 mt-1">
                    MLB: {data.mlb_benchmark.sample_size} pitchers
                  </div>
                )}
                {hasLeagueBenchmark && data.league_benchmark?.sample_size > 0 && (
                  <div className="text-xs opacity-60">
                    League: {data.league_benchmark.sample_size} pitchers
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // No valid data structure
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center`}>
              No performance data available
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};

export default Tile1Performance30dPitcher;
